// Batch 28 · Follow-up 1 · F2 (b) — one Save / Cancel bar per table (not per row), for a table driven by useTableDraft.
// The bar counts the changed rows ("3 rows changed"), and the rows not saved after a Save ("1 row not saved"); Save sends only
// the changed rows (a failed row is retried by the next Save); Cancel puts every changed row back. While anything is unsaved
// the leave guard runs (useUnsavedGuard: links, tab close / reload, the engagement switch, the session warning's Save now).
import React from "react";
import { Button } from "@/components/ui/button";
import { useUnsavedGuard } from "@/components/SaveCancelBar";
import { useSaveBarPresence } from "@/lib/saveBarPresence";   // Batch 29 · M70

export const TSB_TEXT = {
  en: { save: "Save", cancel: "Cancel", retry: "Save again", saving: "Saving…", none: "All changes saved",
        changedOne: "1 row changed", changedMany: "{n} rows changed", failedOne: "1 row not saved", failedMany: "{n} rows not saved" },
  ar: { save: "حفظ", cancel: "إلغاء", retry: "احفظ مرة أخرى", saving: "جارٍ الحفظ…", none: "حُفظت كل التغييرات",
        changedOne: "صف واحد متغيّر", changedMany: "عدد الصفوف المتغيّرة: {n}", failedOne: "صف واحد لم يُحفظ", failedMany: "عدد الصفوف التي لم تُحفظ: {n}" },
};

export function changedLabel(n, lang = "en") {
  const L = TSB_TEXT[lang === "ar" ? "ar" : "en"];
  return n === 1 ? L.changedOne : L.changedMany.replace("{n}", String(n));
}
export function failedLabel(n, lang = "en") {
  const L = TSB_TEXT[lang === "ar" ? "ar" : "en"];
  return n === 1 ? L.failedOne : L.failedMany.replace("{n}", String(n));
}

/** draft = the object useTableDraft returns. onSaved(result) runs after each Save. */
export default function TableSaveBar({ draft, lang = "en", disabled = false, testid = "table-save", onSaved }) {
  const L = TSB_TEXT[lang === "ar" ? "ar" : "en"];
  const n = draft.changedIds.length;
  const failed = draft.failedIds.length;
  const save = async () => { const r = await draft.save(); onSaved && onSaved(r); return r; };
  useUnsavedGuard(n > 0, lang, save);
  useSaveBarPresence();
  return (
    // Step 11c-3 · find 30 (Mars live, 3203-07): two bars stick to the bottom of one dialog; the clean one ("All changes saved")
    // was drawn over the one with a change ("1 row changed"), so the pending change was hidden. A bar with a change is drawn on top.
    <div className={`sticky bottom-0 ${n ? "z-20" : "z-10"} flex flex-wrap items-center justify-end gap-2 rounded border px-3 py-2 text-xs ${n ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "bg-white dark:bg-slate-900"}`}
         data-testid={testid} data-dirty={n ? "1" : "0"} data-changed={n} data-failed={failed} role="status">
      <span className={n ? "font-medium text-amber-900 dark:text-amber-100" : "text-muted-foreground"} data-testid={`${testid}-state`}>
        {draft.saving ? L.saving : n ? changedLabel(n, lang) : L.none}</span>
      {failed > 0 && <span className="font-medium text-red-700 dark:text-red-300" data-testid={`${testid}-failed`}>{failedLabel(failed, lang)}</span>}
      <Button size="sm" variant="outline" disabled={!n || draft.saving || disabled} onClick={draft.cancel} data-testid={`${testid}-cancel`}>{L.cancel}</Button>
      <Button size="sm" disabled={!n || draft.saving || disabled} onClick={save} data-testid={`${testid}-save`}>{failed ? L.retry : L.save}</Button>
    </div>
  );
}

/** The error under a row that was not saved (empty when none). */
export function RowSaveError({ draft, id, testid }) {
  const msg = draft.errorOf(id);
  if (!msg) return null;
  return <div className="mt-0.5 text-[11px] text-red-700 dark:text-red-300" data-testid={testid || `row-save-error-${id}`} role="alert">{msg}</div>;
}
